import * as React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom-v5-compat';

import ByStudent from './by_student'
import ByStandard from './by_standard'

import { ALL_PRACTICE, NO_CLASSROOM, renderNoClassroomEmptyState, } from '../../shared/shared'
import { ClassroomInterface, SkillCategoryInterface, StudentPerformanceData, StateStandardPerformanceData } from '../interfaces';
import { useReportPremiumContext } from '../../shared/report_premium_context';
import {
  Spinner,
} from '../../../../../Shared/index';

interface PerformanceBreakdownProps {
  classroom: ClassroomInterface;
  filteredSkillCategories: SkillCategoryInterface[];
  filteredStateStandards: SkillCategoryInterface[];
  includeELL: boolean;
  momentumScoreGoal: number;
  onSkillCategoryClick: (skillName: string) => void;
  onStudentChange: (student: any) => void;
  practiceScope: string;
  renderHeader: (title: string) => React.ReactNode;
  renderNeedsVerificationEmptyState: () => React.ReactNode;
  renderUpgradeEmptyState: () => React.ReactNode;
  renderVerificationBanner: () => React.ReactNode;
  selectedGradeLevel: string;
  selectedSkillCategoryId: any;
  selectedStateStandardId: any;
  selectedStudentId: any;
  stateStandardsData: {[key: string]: StateStandardPerformanceData[]};
  stateStandardsDataKey: string;
  studentData: {[key: string]: StudentPerformanceData[]};
  studentDataKey: string;
  userNeedsVerification: boolean;
}

const PerformanceBreakdown: React.FC<PerformanceBreakdownProps> = ({
  classroom,
  filteredSkillCategories,
  filteredStateStandards,
  includeELL,
  momentumScoreGoal,
  onSkillCategoryClick,
  onStudentChange,
  practiceScope,
  renderHeader,
  renderNeedsVerificationEmptyState,
  renderUpgradeEmptyState,
  renderVerificationBanner,
  selectedGradeLevel,
  selectedSkillCategoryId,
  selectedStateStandardId,
  selectedStudentId,
  stateStandardsData,
  stateStandardsDataKey,
  studentData,
  studentDataKey,
  userNeedsVerification,
}) => {
  const { hasSchoolOrDistrictPremium } = useReportPremiumContext();
  const navigate = useNavigate();
  const location = useLocation();

  const containerRef = React.useRef<HTMLDivElement>(null)
 
  const byStudent = location.pathname.includes('by_student');

  function handleStudentClick(studentId: number | string) {
    onStudentChange({ value: Number(studentId) });
    navigate('by_standard');
  }

  const selectedSkillCategory = filteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId)
  const selectedStateStandard = filteredStateStandards.find(stateStandard => stateStandard.id === selectedStateStandardId)

  const sharedProps = {
    classroom,
    containerRef,
    includeELL,
    momentumScoreGoal,
    practiceScope,
    selectedSkillCategory
  }

  const routes = (
    <Routes>
      <Route
        element={
          <ByStudent
            {...sharedProps}
            data={studentData[studentDataKey]}
            onStudentClick={handleStudentClick}
            selectedStateStandard={selectedStateStandard}
          />
        }
        path="by_student"
      />
      <Route
        element={
          <ByStandard
            {...sharedProps}
            data={stateStandardsData[stateStandardsDataKey]}
            onSkillCategoryClick={onSkillCategoryClick}
            selectedGradeLevel={selectedGradeLevel}
            studentId={selectedStudentId}
          />
        }
        path="by_standard"
      />
    </Routes>
  )

  if (!classroom || (byStudent && filteredStateStandards.length && !selectedStateStandard)) { return <Spinner /> }

  function renderContent() {
    if (!hasSchoolOrDistrictPremium && practiceScope === ALL_PRACTICE) {
      return renderUpgradeEmptyState()
    }

    if (userNeedsVerification && practiceScope === ALL_PRACTICE) {
      return renderNeedsVerificationEmptyState()
    }

    if (classroom.id === NO_CLASSROOM.id) {
      return renderNoClassroomEmptyState()
    }

    return routes
  }


  return (
    <main className="performance-breakdown" data-testid="performance-breakdown" ref={containerRef}>
      {renderHeader('Performance Breakdown')}
      {renderVerificationBanner()}
      <div className="performance-breakdown-content">
        {renderContent()}
      </div>
    </main>
  );
};

export default PerformanceBreakdown;
